// Import PlanOption type for conversion functions
import type { PlanOption } from "../components/setup/types";
import {
  getProductsAndPrices,
  convertStripeDataToBillingConfig,
  type Plan,
  type BillingConfig,
} from "../services/BillingProductsService";

// Re-export types from ProductsService for backward compatibility
export type { Plan, BillingConfig };

export function getRootRetailer(retailer: string): string {
  const split = retailer.split("_");
  let end = 2;
  if (split.length > 2) {
    end = 2;
  } else {
    end = 1;
  }
  const root = split.slice(0, end).join(" ");
  return root;
}

// Translation keys for plan names and features
export const PLAN_TRANSLATION_KEYS = {
  walmart: {
    basic: "setup.basic",
    starter: "setup.starter",
    pro: "setup.pro",
    enterprise: "setup.enterprise",
    free: "settings.plans.free",
  },
  home_depot: {
    basic: "setup.basic",
    starter: "setup.starter",
    pro: "setup.pro",
    enterprise: "setup.enterprise",
  },
} as const;

export const FEATURE_TRANSLATION_KEYS = {
  walmart: {
    discordAccess: "setup.discordAccess",
    allCategories: "setup.allCategories",
    standardMonitoring: "setup.standardMonitoring",
    advancedMonitoring: "setup.advancedMonitoring",
    prioritySupport: "setup.prioritySupport",
    premiumMonitoring: "setup.premiumMonitoring",
    dedicatedSupport: "setup.dedicatedSupport",
    noAccess: "settings.noAccess",
    upgradeRequired: "settings.upgradeRequired",
  },
  home_depot: {
    discordAccess: "setup.discordAccess",
    allCategories: "setup.allCategories",
    standardMonitoring: "setup.standardMonitoring",
    advancedMonitoring: "setup.advancedMonitoring",
    prioritySupport: "setup.prioritySupport",
    premiumMonitoring: "setup.premiumMonitoring",
    dedicatedSupport: "setup.dedicatedSupport",
  },
} as const;

// Dynamic billing configuration - fetched from Stripe
let cachedBillingConfig: BillingConfig | null = null;
let configPromise: Promise<BillingConfig> | null = null;

export const getBillingConfig = async (): Promise<BillingConfig> => {
  if (cachedBillingConfig) {
    return cachedBillingConfig;
  }

  if (configPromise) {
    return configPromise;
  }

  configPromise = (async () => {
    try {
      const data = await getProductsAndPrices();
      const config = convertStripeDataToBillingConfig(data);
      cachedBillingConfig = config;
      return config;
    } catch (error) {
      console.error("Failed to fetch billing config:", error);
      // Return empty config as fallback
      return {};
    }
  })();

  return configPromise;
};

// Clear cache when needed (e.g., after plan changes)
export const clearBillingConfigCache = () => {
  cachedBillingConfig = null;
  configPromise = null;
};

// Landing page specific plans (simplified for marketing)
export const LANDING_PAGE_PLANS = [
  {
    id: "basic",
    name: "Basic",
    price: 34.99,
    discountedPrice: 17.5,
    popular: false,
    description: "Perfect for getting started",
    features: [
      "1 Walmart + 1 Local Big Box Hardware Retailer Location, each with 4 extra nearby",
      "Discord Community Access",
      "Push Notifications & Alerts",
      "Favorites & Tracking",
      "Mobile App Access",
      "Product Search & Leads",
    ],
  },
  {
    id: "plus",
    name: "Plus",
    price: 59.99,
    discountedPrice: 30,
    popular: false,
    description: "More stores, more savings",
    features: [
      "3 Walmart + 3 Local Big Box Hardware Retailer Locations, plus 7 extra nearby of each",
      "2 Target + 1 Store each: Lowe's, Dollar General, Costco & Best Buy",
      "More saved deals and price alerts",
      "Exact local prices, inventory, and aisle details",
      "Everything in Basic included",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: 99,
    discountedPrice: 49.5,
    popular: true,
    description: "For serious deal hunters",
    features: [
      "5 Walmart + 5 Local Big Box Hardware Retailer Locations, plus 15 extra nearby Walmarts and 10 hardware",
      "5 Target + 5 Dollar General Locations",
      "5 Lowe's + 3 Costco Stores",
      "Advanced local deal intelligence",
      "More saved deals and price alerts",
      "Exact local prices and inventory",
      "Aisle-level local details",
      "Advanced confidence and prediction insights",
      "Higher tracking limits",
      "Everything in Basic included",
      "Priority Support",
    ],
  },
];

// Utility functions
export const getPlansForRetailer = async (
  retailer: string
): Promise<Plan[]> => {
  const config = await getBillingConfig();
  return config[retailer] || [];
};

export const getPlanById = async (
  retailer: string,
  planId: string
): Promise<Plan | undefined> => {
  const plans = await getPlansForRetailer(retailer);
  return plans.find((plan) => plan.id === planId);
};

export const getPriceIdForPlan = async (
  retailer: string,
  planId: string
): Promise<string> => {
  const plan = await getPlanById(retailer, planId);
  return plan?.priceId || "";
};

// Map plan IDs to Stripe price IDs (for backward compatibility)
export const PLAN_TO_PRICE_ID = {
  basic: "price_1RnYYoG2cPZes0aX5EfNmKLM",
  starter: "price_1RdzZJG2cPZes0aX1yJocrdW",
  pro: "price_1RdzZUG2cPZes0aXCDnVJAIG",
  enterprise: "price_1RdzZiG2cPZes0aXyk67ntic",
  "home_depot-basic": "price_1S59QgG2cPZes0aXLizXj8Rx",
};

// Map Stripe price IDs to plan info (for server-side compatibility)
export const PRICE_ID_TO_PLAN = {
  price_1RnYYoG2cPZes0aX5EfNmKLM: { id: "basic", retailer: "walmart" },
  price_1RdzZJG2cPZes0aX1yJocrdW: { id: "starter", retailer: "walmart" },
  price_1RdzZUG2cPZes0aXCDnVJAIG: { id: "pro", retailer: "walmart" },
  price_1RdzZiG2cPZes0aXyk67ntic: { id: "enterprise", retailer: "walmart" },
  price_1S59QgG2cPZes0aXLizXj8Rx: { id: "basic", retailer: "home_depot" },
};

// Convert Plan to PlanOption (for backward compatibility)
export const planToPlanOption = (plan: Plan): PlanOption => ({
  id: plan.id,
  name: plan.name,
  price: plan.price,
  features: plan.features,
  popular: plan.popular,
  priceId: plan.priceId,
  storeLimit: plan.storeLimit,
  trackedProducts: plan.trackedProducts,
  retailer: plan.retailer,
  frequency: plan.frequency,
});

// Convert PlanOption to Plan
export const planOptionToPlan = (planOption: PlanOption): Plan => ({
  id: planOption.id,
  name: planOption.name,
  price: planOption.price,
  features: planOption.features,
  popular: planOption.popular,
  priceId: planOption.priceId,
  storeLimit: planOption.storeLimit,
  trackedProducts: planOption.trackedProducts,
  retailer: planOption.retailer,
  frequency: planOption.frequency || "monthly",
  displayName: planOption.name,
});

// Get plans as PlanOption array for a specific retailer
export const getPlanOptionsForRetailer = async (
  retailer: string
): Promise<PlanOption[]> => {
  const plans = await getPlansForRetailer(retailer);
  return plans.map(planToPlanOption);
};
